"use client";

import React, { useState } from "react";
import { useSWRConfig } from "swr";
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import { Button, Input, Alert } from "@excellent/ui";
import { Fingerprint, ShieldCheck, Chrome, Apple, ArrowRight, Mail } from "@excellent/ui/icons";
import { apiCall, apiKey, useApi, ApiError } from "@excellent/ui/api";
import { auth } from "@excellent/ui/api/contracts/auth";
import { account } from "@excellent/ui/api/contracts/account";
import { DarkRoom, BrandLockup } from "@excellent/ui/blocks/play/dark-room";
import { Logo } from "../chrome/logo";
import type { AuthPreviewState } from "./preview-states";
import { authenticationDestination } from "../../../../packages/ui/src/blocks/authentication-state";

type Mode = "welcome" | "login" | "register" | "recover";

// Dev-preview fixtures (see ./preview-states + @/dev/preview): static stand-ins
// shaped like the real thing (10 × 24-char base64url recovery codes) so the
// `codes` / `signed-in` states — normally reachable only mid-ceremony — render
// for /dev and the screen-map. Never issued, never accepted anywhere.
const PREVIEW_RECOVERY_CODES = [
  "Zx4tQ9vKm2Lr8sWnYb31cAdE",
  "pF7hJc0uNq6TgXe2Rk9mBw5v",
  "aU3sVd8yHt1PoLi6Cn4zMj7q",
  "eK9bGf2xWm5DrZa8Qv0tYl3n",
  "hN6cTp4jSy7FuBo1Xw3kEd9r",
  "mQ1vLz5gCk8HxJe4Ta6nUf2b",
  "sD8wRb3qPn0GvKu7Ye5jZc1t",
  "uJ5xEh7dAf2VbMs9Wq4rTk6y",
  "wB2gYn9kDl4XcQt6Fe8vHp0z",
  "yT7fMv1rGw3JzNb5Ck9sXa4d",
];
const PREVIEW_EMAIL = "you@example.com";

/**
 * Login — the front door, in the marketing-site (excellent.so) design language
 * so the seam from www → product disappears. The DarkRoom on the warm #171717
 * floor, lit by one soft top breath + the indigo "dawn" rising from below, with
 * the 8-star starfield behind the welcome statement. Geist body + a Fraunces
 * display headline (`.first-run-*`), the tight [bracket] eyebrow, and the
 * brand blue→purple primary (`<Button variant="graphite">` — recolored from the
 * old neutral graphite to the brand hue). Reference: /styleguide/onboarding-language.
 *
 * The WebAuthn ceremony is UNCHANGED — only the chrome moved. Excellent is
 * passwordless: the hero CTA is a passkey; the social row is honored from the
 * reference but flagged roadmap (disabled).
 */
export function LoginClient({ preview }: { preview?: AuthPreviewState }) {
  const { data: me, error: sessionError } = useApi(auth.me, {});
  const [destination] = useState(() =>
    authenticationDestination(
      typeof window === "undefined"
        ? null
        : new URLSearchParams(window.location.search).get("returnTo"),
    ),
  );
  // `preview` (dev-gated, passed only when @/dev/preview is active) forces one of
  // the six visual states for /dev + the screen-map: the four Mode states seed the
  // initial mode; `codes` / `signed-in` seed their fixtures below. GET-only — the
  // forms still work normally if touched, but nothing is pre-submitted.
  const [mode, setMode] = useState<Mode>(
    preview === "login" || preview === "register" || preview === "recover"
      ? preview
      : !preview &&
          typeof window !== "undefined" &&
          new URLSearchParams(window.location.search).has("returnTo")
        ? "login"
        : "welcome",
  );
  const [handle, setHandle] = useState("");
  const [displayName, setDisplayName] = useState("");
  // Opt-in email captured at sign-up so the vendor knows who's using the app —
  // the one identified signal the otherwise-anonymous, local-first app emits.
  // (Seeded in codes-preview so the email-a-backup affordance is visible.)
  const [email, setEmail] = useState(preview === "codes" ? PREVIEW_EMAIL : "");
  const [recoveryCode, setRecoveryCode] = useState("");
  // One-time recovery codes returned on first registration — shown once, saved
  // by the user, then dismissed into the signed-in state.
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(
    preview === "codes" ? PREVIEW_RECOVERY_CODES : null,
  );
  // Emailed backup of the recovery codes (only when the user shared an email).
  const [backupState, setBackupState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The shell mounts a custom SWR cache provider; the top-level global
  // `mutate` is a silent no-op against it. Go through the provider-scoped
  // mutate (memory project_swr_custom_provider_mutate).
  const { mutate } = useSWRConfig();

  const supported = typeof window !== "undefined" ? browserSupportsWebAuthn() : true;

  async function refreshMe() {
    await mutate(apiKey(auth.me, {}));
  }

  async function enterWorkspace() {
    await refreshMe();
    if (!preview) window.location.assign(destination);
  }

  async function doRegister(): Promise<string[] | null> {
    const { options } = await apiCall(auth.registerOptions, {
      body: { handle: handle.trim(), displayName: displayName.trim() || handle.trim() },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const attestation = await startRegistration({ optionsJSON: options as any });
    const res = await apiCall(auth.registerVerify, {
      body: { handle: handle.trim(), response: attestation as unknown as Record<string, unknown> },
    });
    return res.recoveryCodes ?? null;
  }

  // Best-effort: if the user shared an email at sign-up, send it to the central
  // sink so the vendor knows who's using the app. Opt-in (empty → no-op) and
  // fire-and-forget — a capture failure must NEVER affect sign-in.
  async function notifySignup() {
    const e = email.trim();
    if (!e) return;
    try {
      await apiCall(account.notifySignup, {
        body: {
          email: e,
          name: displayName.trim() || undefined,
          handle: handle.trim() || undefined,
          source: "app-signup",
        },
      });
    } catch {
      // swallow — the user is signed in regardless
    }
  }

  // Best-effort: email the user a backup of their one-time recovery codes (only
  // if they shared an email at sign-up). A mailer failure is reported honestly so
  // the user knows to save the codes manually — it never blocks entering the app.
  async function emailBackup() {
    const e = email.trim();
    if (!e || !recoveryCodes?.length) return;
    setBackupState("sending");
    try {
      const { ok } = await apiCall(account.recoveryEmail, {
        body: { email: e, codes: recoveryCodes, handle: handle.trim() || undefined },
      });
      setBackupState(ok ? "sent" : "error");
    } catch {
      setBackupState("error");
    }
  }

  async function doRecover() {
    const { options } = await apiCall(auth.recoverOptions, {
      body: { handle: handle.trim(), recoveryCode: recoveryCode.trim() },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const attestation = await startRegistration({ optionsJSON: options as any });
    await apiCall(auth.recoverVerify, {
      body: { handle: handle.trim(), response: attestation as unknown as Record<string, unknown> },
    });
  }

  async function doLogin() {
    const { options } = await apiCall(auth.loginOptions, {
      body: handle.trim() ? { handle: handle.trim() } : {},
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const assertion = await startAuthentication({ optionsJSON: options as any });
    await apiCall(auth.loginVerify, {
      body: { response: assertion as unknown as Record<string, unknown> },
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === "register") {
        const codes = await doRegister();
        void notifySignup(); // fire-and-forget; never blocks entering the app
        if (codes && codes.length) {
          // Show the save-your-codes panel before entering the app.
          setRecoveryCodes(codes);
          return;
        }
      } else if (mode === "recover") {
        await doRecover();
      } else {
        await doLogin();
      }
      await enterWorkspace();
    } catch (err) {
      // WebAuthn ceremony cancel/NotAllowed throws DOMException; API failures throw ApiError.
      if (err instanceof ApiError) setError(err.error);
      else if (err instanceof Error) setError(err.message);
      else setError("Something went wrong with the passkey.");
    } finally {
      setBusy(false);
    }
  }

  async function onLogout() {
    setBusy(true);
    try {
      await apiCall(auth.logout, {});
      await refreshMe();
    } finally {
      setBusy(false);
    }
  }

  function go(next: Mode) {
    setMode(next);
    setError(null);
  }

  // One-time recovery codes — shown once after first registration, before the
  // app. A lost device + a saved code is the only way back into the account.
  if (recoveryCodes) {
    return (
      <Room>
        <div className="relative z-[1] flex flex-1 w-full items-center justify-center px-6">
          <div className="w-full max-w-[26rem]">
            <Logo size={28} className="text-foreground" />
            <h1 className="first-run-display mt-5 text-foreground">Save your recovery codes</h1>
            <p className="first-run-deck mt-2 text-muted-foreground">
              Passkeys live on your device. If you lose it, a recovery code is the only way back
              into your account. Store these somewhere safe — they won’t be shown again.
            </p>
            <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2.5 rounded-lg border border-white/10 bg-white/[0.03] p-4 font-mono">
              {recoveryCodes.map((c) => (
                <li key={c} className="type-body tracking-wide text-foreground">
                  {c}
                </li>
              ))}
            </ul>
            {email.trim() &&
              (backupState === "sent" ? (
                <p className="mt-4 type-subtext leading-body text-muted-foreground">
                  A backup was emailed to {email.trim()}. Anyone with these codes and your computer
                  can get in — keep that email private, or delete it once you’ve saved them
                  elsewhere.
                </p>
              ) : (
                <div className="mt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={backupState === "sending"}
                    onClick={emailBackup}
                    className="w-full justify-center gap-2"
                  >
                    <Mail strokeWidth={1.75} className="h-4 w-4" />
                    {backupState === "sending"
                      ? "Emailing a backup…"
                      : backupState === "error"
                        ? "Couldn’t send — try again"
                        : `Email a backup to ${email.trim()}`}
                  </Button>
                  {backupState === "error" && (
                    <p className="mt-1.5 px-1 type-subtext text-muted-foreground">
                      Save your codes manually — the backup email didn’t go out.
                    </p>
                  )}
                </div>
              ))}
            <div className="mt-5 flex gap-2.5">
              <Button
                variant="outline"
                className="flex-1 justify-center"
                onClick={() => navigator.clipboard?.writeText(recoveryCodes.join("\n"))}
              >
                Copy codes
              </Button>
              <Button
                variant="graphite"
                className="flex-1 justify-center gap-2"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    setRecoveryCodes(null);
                    await enterWorkspace();
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                I’ve saved them
                <ArrowRight strokeWidth={2} className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Room>
    );
  }

  // Signed-in state — same room, a centered confirmation. In preview, the forced
  // state wins both ways: `signed-in` renders it with fixture identity even when
  // logged out, and any other forced state suppresses it even when logged in.
  if (preview ? preview === "signed-in" : !sessionError && me?.authenticated) {
    return (
      <Room>
        <div className="relative z-[1] flex flex-1 w-full items-center justify-center px-6">
          <div className="flex w-full max-w-[22rem] flex-col items-center gap-5 text-center">
            <Logo size={28} className="text-foreground" />
            <p className="type-body text-muted-foreground">
              Signed in as <span className="text-foreground">{me?.handle ?? "you"}</span> ·
              workspace{" "}
              <span className="text-foreground">
                {me?.workspaceId == null ? "not selected" : `#${me.workspaceId}`}
              </span>
            </p>
            <Button
              variant="graphite"
              onClick={() => {
                if (!preview) window.location.assign(destination);
              }}
            >
              Continue to workspace
            </Button>
            <Button variant="outline" onClick={onLogout} disabled={busy}>
              {busy ? "Signing out…" : "Sign out"}
            </Button>
          </div>
        </div>
      </Room>
    );
  }

  // The welcome splash — the confident first impression in the site's voice.
  // Centered over the starfield: the logotype lockup, the Fraunces "Locals only."
  // statement (echoing the marketing hero), a tight two-line deck, and a colored
  // primary "set up" stacked over a quiet "sign in".
  if (mode === "welcome") {
    return (
      <Room starfield>
        <div className="relative z-[1] mx-auto flex w-full max-w-[46rem] flex-1 flex-col items-center justify-center px-6 text-center">
          <BrandLockup markSize={22} />
          <h1 className="first-run-display first-run-hero first-run-ink mt-10">
            Make work verifiable.
          </h1>
          <p className="first-run-deck mt-6 max-w-[34rem] text-muted-foreground">
            Define the work, review its contract, and inspect evidence and verification results.
          </p>
          <div className="mt-9 flex flex-col items-center gap-3">
            {/* Optical balance: the trailing arrow draws in a 16px box (viewBox
                x∈[5,19]/24) leaving ~3–4px of whitespace right of its tip, so the
                atom's symmetric px-3 reads right-heavy against the flush label.
                Nudge left padding to 16px; right holds the variant's 12px. */}
            <Button
              variant="default"
              size="lg"
              onClick={() => go("register")}
              className="gap-2 has-[>svg]:pl-4 has-[>svg]:pr-3"
            >
              Set up your workspace
              <ArrowRight strokeWidth={2} className="h-4 w-4" />
            </Button>
            <Button size="lg" variant="ghost" onClick={() => go("login")}>
              I already have an account
            </Button>
          </div>
        </div>
        <WelcomeFooter />
      </Room>
    );
  }

  const isRegister = mode === "register";
  const isRecover = mode === "recover";

  // The focused passkey card — centered on the room, one column.
  return (
    <Room>
      <div className="relative z-[1] flex flex-1 w-full items-center justify-center px-6 py-12">
        <div className="w-full max-w-[348px]">
          <div className="flex flex-col items-center text-center">
            <button
              type="button"
              onClick={() => go("welcome")}
              className="rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-label="Back to start"
            >
              <Logo size={28} className="text-foreground" />
            </button>
            <h1 className="first-run-display mt-5 text-foreground">
              {isRegister
                ? "Set up your workspace"
                : isRecover
                  ? "Recover your account"
                  : "Welcome back"}
            </h1>
            <p className="first-run-deck mt-2 text-muted-foreground">
              {isRegister
                ? "Create an account with a passkey to access your verification workspace."
                : isRecover
                  ? "Lost your device? Use a recovery code to add a new passkey."
                  : "Your device authenticates you. No password to remember."}
            </p>
          </div>

          {!supported && (
            <Alert variant="default" className="mt-6">
              This browser doesn’t support passkeys (WebAuthn). Use a current Chrome, Safari, or
              Firefox.
            </Alert>
          )}

          <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-2.5">
            {isRegister && (
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                autoFocus
                size="lg"
              />
            )}
            <Input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder={isRegister ? "Workspace handle" : "Handle"}
              autoComplete="username webauthn"
              autoFocus={!isRegister}
              required={isRegister || isRecover}
              size="lg"
            />
            {isRegister && (
              <>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional)"
                  autoComplete="email"
                  size="lg"
                />
                <p className="px-1 type-subtext leading-body text-muted-foreground">
                  Optional. Shares your email, name, and handle with Excellent so we can send
                  product updates and know who’s using the app. Your business data stays on your
                  machine.
                </p>
              </>
            )}
            {isRecover && (
              <Input
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value)}
                placeholder="one of the codes you saved"
                autoComplete="one-time-code"
                required
                size="lg"
              />
            )}

            {error && <Alert variant="destructive">{error}</Alert>}

            <Button
              type="submit"
              variant="graphite"
              size="lg"
              disabled={busy || !supported}
              className="mt-1 w-full justify-center gap-2"
            >
              <Fingerprint strokeWidth={1.75} className="h-4 w-4" />
              {busy
                ? "Waiting for passkey…"
                : isRegister
                  ? "Create with a passkey"
                  : isRecover
                    ? "Recover with a passkey"
                    : "Continue with passkey"}
            </Button>
          </form>
          <p className="mt-3 flex items-center justify-center gap-1.5 type-body text-muted-foreground">
            <ShieldCheck strokeWidth={1.5} className="h-3.5 w-3.5" />
            Passwordless — your fingerprint or face unlocks it.
          </p>

          {/* Sign-in offers the social row (honored from the reference, roadmap). */}
          {!isRegister && !isRecover && (
            <>
              <div className="my-5 flex items-center gap-3">
                <span className="h-px flex-1 bg-white/10" />
                <span className="type-body text-muted-foreground">or</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <div className="flex gap-2.5">
                <Button variant="outline" disabled className="flex-1 justify-center gap-2">
                  <Chrome strokeWidth={1.5} className="h-4 w-4" />
                  Google
                </Button>
                <Button variant="outline" disabled className="flex-1 justify-center gap-2">
                  <Apple strokeWidth={1.5} className="h-4 w-4" />
                  Apple
                </Button>
              </div>
              <p className="mt-2.5 text-center type-body text-muted-foreground">
                Social sign-in is on the roadmap.
              </p>
            </>
          )}

          {/* Mode toggle */}
          <div className="mt-7 text-center type-body text-muted-foreground">
            {isRecover ? (
              <>
                Got your device back?{" "}
                <button
                  type="button"
                  onClick={() => go("login")}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  Sign in
                </button>
              </>
            ) : isRegister ? (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => go("login")}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  Log in
                </button>
              </>
            ) : (
              <>
                New to Excellent?{" "}
                <button
                  type="button"
                  onClick={() => go("register")}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  Create an account
                </button>
              </>
            )}
          </div>
          {!isRegister && !isRecover && (
            <p className="mt-2 text-center type-body text-muted-foreground">
              Lost your device?{" "}
              <button
                type="button"
                onClick={() => go("recover")}
                className="text-primary underline-offset-4 hover:underline"
              >
                Use a recovery code
              </button>
            </p>
          )}
        </div>
      </div>
    </Room>
  );
}

/** The front-door footer — legal + the site + copyright, mirroring the marketing
 *  site's (excellent.so) bottom bar so the seam from www → product disappears.
 *  Legal pages live on the marketing site, so the links are absolute and open in
 *  a new tab. Pinned to the bottom of the welcome room: the hero content above it
 *  is `flex-1`, so this sits on the floor while the statement stays centered. */
function WelcomeFooter() {
  return (
    <footer className="relative z-[1] flex flex-col items-center gap-2 px-6 pb-8 text-center">
      <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 type-subtext text-muted-foreground">
        <a
          href="https://excellent.so/privacy"
          target="_blank"
          rel="noreferrer"
          className="transition-colors hover:text-foreground"
        >
          Privacy
        </a>
        <span aria-hidden className="text-muted-foreground/40">
          ·
        </span>
        <a
          href="https://excellent.so/terms"
          target="_blank"
          rel="noreferrer"
          className="transition-colors hover:text-foreground"
        >
          Terms
        </a>
        <span aria-hidden className="text-muted-foreground/40">
          ·
        </span>
        <a
          href="https://excellent.so"
          target="_blank"
          rel="noreferrer"
          className="transition-colors hover:text-foreground"
        >
          excellent.so
        </a>
      </nav>
      <p className="type-subtext text-muted-foreground/70">
        © {new Date().getFullYear()} Excellent Software, LLC · Your data, your machine.
      </p>
    </footer>
  );
}

/** The shared launch shell — the DarkRoom in the marketing-site language (warm
 *  floor + indigo dawn + vignette, with the starfield on the welcome statement)
 *  under a forced `.dark` scope + `.first-run` (Geist), shortened to clear the
 *  desktop title bar when present. */
function Room({ children, starfield }: { children: React.ReactNode; starfield?: boolean }) {
  return (
    <div className="first-run dark relative flex min-h-[calc(100vh-var(--desktop-titlebar-h,0px))] w-full flex-col overflow-hidden text-foreground">
      <DarkRoom dawn vignette starfield={starfield} />
      {children}
    </div>
  );
}
